/**
 * La scheda «Liquidazioni»: le bozze che il giro prepara a fine periodo, e i tre passi che le portano al bonifico
 * (approvata → fattura ricevuta → pagata). Ritenuta e cassa ci sono solo se il regime del partner le prevede.
 */

import React, { useMemo, useState } from 'react';
import {
  Box, Button, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';
import { SoftChip, type SoftChipTone } from '../SoftChip';
import type { LiquidazioneDoc, StatoLiquidazione } from '@shared/partner/tipi';
import { avanzaLiquidazione, type ConId, type DatiProgrammaPartner } from '../../services/partnerConsoleService';
import { DialogoCampi } from './DialogoCampi';
import type { Esegui } from './esegui';
import { oggi, useFormati } from './formati';

const STATI: StatoLiquidazione[] = ['bozza', 'approvata', 'fattura_ricevuta', 'pagata'];
const TONO: Record<StatoLiquidazione, SoftChipTone> = { bozza: 'warning', approvata: 'info', fattura_ricevuta: 'primary', pagata: 'success' };

interface Props {
  dati: DatiProgrammaPartner;
  esegui: Esegui;
}

export const SchedaLiquidazioni: React.FC<Props> = ({ dati, esegui }) => {
  const { t } = useTranslation('partner');
  const f = useFormati();
  const [filtroStato, setFiltroStato] = useState<StatoLiquidazione | 'tutti'>('tutti');
  const [fattura, setFattura] = useState<ConId<LiquidazioneDoc> | null>(null);
  const [pagata, setPagata] = useState<ConId<LiquidazioneDoc> | null>(null);

  const nomePartner = useMemo(() => new Map(dati.partner.map((p) => [p.id, p.nome])), [dati.partner]);
  const elenco = useMemo(
    () => dati.liquidazioni
      .filter((l) => filtroStato === 'tutti' || l.stato === filtroStato)
      .sort((a, b) => STATI.indexOf(a.stato) - STATI.indexOf(b.stato) || b.periodo_dal.localeCompare(a.periodo_dal)),
    [dati.liquidazioni, filtroStato],
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
        <TextField select size="small" value={filtroStato} onChange={(e) => setFiltroStato(e.target.value as StatoLiquidazione | 'tutti')} sx={{ minWidth: 190 }}>
          <MenuItem value="tutti">{t('filtri.tutti_gli_stati')}</MenuItem>
          {STATI.map((s) => <MenuItem key={s} value={s}>{t(`stato_liquidazione.${s}`)}</MenuItem>)}
        </TextField>
      </Stack>
      <Paper variant="outlined" sx={{ borderColor: tokens.color.line, bgcolor: tokens.color.paper, overflow: 'hidden' }}>
        {elenco.length === 0 ? (
          <Typography variant="body2" sx={{ p: 4, color: tokens.color.ash, textAlign: 'center' }}>{t('vuoti.liquidazioni')}</Typography>
        ) : (
          <Table size="small" sx={{ '& td': { py: 1.5 }, '& tr:last-child td': { borderBottom: 0 } }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('colonne.partner')}</TableCell>
                <TableCell>{t('colonne.periodo')}</TableCell>
                <TableCell align="right">{t('colonne.imponibile')}</TableCell>
                <TableCell align="right">{t('colonne.ritenuta')}</TableCell>
                <TableCell align="right">{t('colonne.cassa')}</TableCell>
                <TableCell align="right">{t('colonne.netto')}</TableCell>
                <TableCell>{t('colonne.stato')}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {elenco.map((l) => (
                <TableRow key={l.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.graphite }}>{nomePartner.get(l.partner_id) ?? l.partner_id}</Typography>
                    <Typography variant="caption" sx={{ color: tokens.color.mist }}>{t('liquidazioni.movimenti', { count: l.movimenti })}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{t('liquidazioni.periodo', { dal: f.mese(l.periodo_dal), al: f.mese(l.periodo_al) })}</Typography>
                  </TableCell>
                  <TableCell align="right">{f.soldi(l.imponibile_centesimi, l.valuta)}</TableCell>
                  <TableCell align="right">{l.ritenuta_centesimi ? f.soldi(l.ritenuta_centesimi, l.valuta) : '—'}</TableCell>
                  <TableCell align="right">{l.cassa_partner_centesimi ? f.soldi(l.cassa_partner_centesimi, l.valuta) : '—'}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{f.soldi(l.netto_centesimi, l.valuta)}</Typography>
                    {l.cassa_nostra_centesimi > 0 && (
                      <Typography variant="caption" sx={{ color: tokens.color.mist, display: 'block' }}>
                        {t('liquidazioni.costo_nostro', { importo: f.soldi(l.imponibile_centesimi + l.iva_centesimi + l.cassa_nostra_centesimi, l.valuta) })}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <SoftChip label={t(`stato_liquidazione.${l.stato}`)} tone={TONO[l.stato]} />
                    {l.fattura_partner?.numero && (
                      <Typography variant="caption" sx={{ color: tokens.color.ash, display: 'block', mt: 0.5 }}>
                        {t('liquidazioni.fattura', { numero: l.fattura_partner.numero, data: f.giorno(l.fattura_partner.data) })}
                      </Typography>
                    )}
                    {l.pagata_il && (
                      <Typography variant="caption" sx={{ color: tokens.color.ash, display: 'block' }}>{t('liquidazioni.pagata_il', { data: f.giorno(l.pagata_il) })}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {l.stato === 'bozza' && (
                        <Button size="small" variant="outlined" onClick={() => void esegui(() => avanzaLiquidazione({ liquidazione_id: l.id, azione: 'approva' }))} sx={{ textTransform: 'none' }}>
                          {t('azioni.approva')}
                        </Button>
                      )}
                      {l.stato === 'approvata' && (
                        <Button size="small" onClick={() => setFattura(l)} sx={{ textTransform: 'none' }}>{t('azioni.fattura_ricevuta')}</Button>
                      )}
                      {(l.stato === 'approvata' || l.stato === 'fattura_ricevuta') && (
                        <Button size="small" variant="outlined" onClick={() => setPagata(l)} sx={{ textTransform: 'none' }}>{t('azioni.pagata')}</Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <DialogoCampi
        aperto={!!fattura}
        titolo={t('dialogo_fattura.titolo')}
        campi={[
          { nome: 'numero', etichetta: t('dialogo_fattura.numero'), tipo: 'testo', obbligatorio: true },
          { nome: 'data', etichetta: t('dialogo_fattura.data'), tipo: 'data', obbligatorio: true, iniziale: oggi() },
        ]}
        etichettaConferma={t('azioni.fattura_ricevuta')}
        onConferma={(v) => (fattura
          ? esegui(() => avanzaLiquidazione({ liquidazione_id: fattura.id, azione: 'fattura_ricevuta', numero_fattura: v.numero.trim(), data: v.data }))
          : Promise.resolve(false))}
        onChiudi={() => setFattura(null)}
      />
      <DialogoCampi
        aperto={!!pagata}
        titolo={t('dialogo_pagata.titolo')}
        campi={[{ nome: 'data', etichetta: t('dialogo_pagata.data'), tipo: 'data', obbligatorio: true, iniziale: oggi() }]}
        etichettaConferma={t('azioni.pagata')}
        onConferma={(v) => (pagata ? esegui(() => avanzaLiquidazione({ liquidazione_id: pagata.id, azione: 'pagata', data: v.data })) : Promise.resolve(false))}
        onChiudi={() => setPagata(null)}
      />
    </Box>
  );
};
